import React, { useEffect, useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import styles from "../../DeviceHealth/FrequencyBucketTable.module.css";
import tableStyles from "./DeviceStateCityTable.module.css";
import CustomModal from "../../../../../Shared/CustomModal";
import TablePagination from "../../DeviceHealth/TablePagination";
import { useLatencyContextData } from "../context/Context";
import useApiRows from "../useApiRows";
import { SWITCHES, filterFromSwitches, getStandardInterval } from "../deviceStates";
import { IntervalCell, IntervalHeader, StatusCells, SwitchChip } from "./DeviceStateCells";
import { getDeviceStateComboVehicles } from "../LatencyService";

export const DEVICE_STATE_DEPOT_MODAL_ID = "DeviceStateDepotVehicles";

const ROWS_PER_PAGE = 10; // fixed, not user-changeable
const COLUMNS = ["VIN No.", "Reg No.", "Mains", "Ignition", "Emergency"];
const COL_COUNT = COLUMNS.length + 1;

const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

const toOption = (value) => [{ label: value, value }];

/*
 * selection: { city, depot, switches } — vehicles of that depot in the switch combination.
 * The toolbar switches start from the table's combination and can be flipped here
 * (the table's header switches are not changed).
 */
export default function DeviceStateDepotPopover({ selection }) {
    const { GlobalFilter } = useLatencyContextData();
    const [currentPage, setCurrentPage] = useState(1);
    const { city, depot } = selection || {};
    // combination changed inside the popover; null = the table's combination
    const [override, setOverride] = useState(null);
    const switches = override ?? selection?.switches;
    const standard = switches ? getStandardInterval(switches) : null;

    // every open starts from the table's combination
    useEffect(() => setOverride(null), [selection]);

    const toggleSwitch = (key) =>
        setOverride((prev) => {
            const current = prev ?? selection.switches;
            return { ...current, [key]: !current[key] };
        });

    const [vehicles, isLoading] = useApiRows(
        getDeviceStateComboVehicles,
        // popover's own combination overrides the GlobalFilter switches
        { ...GlobalFilter, ...(switches ? filterFromSwitches(switches) : {}), city: toOption(city), depot: toOption(depot) },
        !depot
    );

    useEffect(() => setCurrentPage(1), [vehicles]);

    const totalItems = vehicles.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedVehicles = useMemo(() => {
        const start = (safePage - 1) * ROWS_PER_PAGE;
        return vehicles.slice(start, start + ROWS_PER_PAGE);
    }, [vehicles, safePage]);

    return (
        <CustomModal
            id={DEVICE_STATE_DEPOT_MODAL_ID}
            title={depot ? `${depot} Depot · Vehicles` : "Vehicles"}
            size="modal-xl"
            header={true}
            closeBtnClass="btn ms_btn btn-default"
        >
            {depot && (
                <div className={styles.popoverBody}>
                    <div className={styles.popoverMeta}>
                        <span className={styles.metaChip}>
                            <MapPin size={11} />
                            {city} · {depot}
                        </span>
                        {SWITCHES.map((s) => (
                            <SwitchChip
                                key={s.key}
                                item={s}
                                checked={switches[s.key]}
                                onToggle={() => toggleSwitch(s.key)}
                            />
                        ))}
                        <span className={styles.metaCount}>
                            {isLoading ? "Loading..." : `${totalItems} vehicles`}
                        </span>
                    </div>

                    <div className={styles.popoverTableWrapper}>
                        <table className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable} ${styles.popoverTable}`}>
                            <colgroup>
                                <col className={tableStyles.colVin} />
                                <col className={tableStyles.colReg} />
                            </colgroup>

                            <thead className="sticky-top">
                                <tr>
                                    {COLUMNS.map((c) => <th key={c}>{c}</th>)}
                                    <IntervalHeader standard={standard} />
                                </tr>
                            </thead>

                            <tbody>
                                {isLoading ? (
                                    <tr>
                                        <td colSpan={COL_COUNT}>
                                            <div className={styles.loadingState}>
                                                <span className={styles.spinner} />
                                                <span>Loading...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : totalItems === 0 ? (
                                    <tr>
                                        <td colSpan={COL_COUNT}>
                                            <div className={styles.loadingState}>No vehicles in this combination</div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedVehicles.map((v) => (
                                        <tr key={v.vin} className={styles.locationRow}>
                                            <td className={styles.cityCell}>
                                                <span className={styles.vinText} title={v.vin}>{displayValue(v.vin)}</span>
                                            </td>
                                            <td>{displayValue(v.regNumber)}</td>
                                            <StatusCells values={v} />
                                            <IntervalCell actual={v.actualInterval} standard={standard} />
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {!isLoading && (
                        <TablePagination
                            page={safePage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            pageSize={ROWS_PER_PAGE}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </div>
            )}
        </CustomModal>
    );
}
